/**
 * StatusDots — a count drawn as one dot per thing in the color of its state, so one glance
 * reads how many there are and how many are in trouble. Past the dots a tile has room for, a
 * bar of the same proportions.
 */

import type { ReactElement } from 'react';
import React from 'react';

import { cn } from '@/lib/utils';
import type { LauncherFigurePart } from '@/types/panels';
import { LauncherTileSize } from '@/types/panels';

interface Props {
    parts: LauncherFigurePart[];
    size: LauncherTileSize;
}

const DOT: Record<LauncherTileSize, string> = {
    [LauncherTileSize.Large]: 'size-2.5',
    [LauncherTileSize.Medium]: 'size-2',
    [LauncherTileSize.Small]: 'size-1.5',
};

const DOT_GAP: Record<LauncherTileSize, string> = {
    [LauncherTileSize.Large]: 'gap-1.5',
    [LauncherTileSize.Medium]: 'gap-1',
    [LauncherTileSize.Small]: 'gap-1',
};

/** Two rows at the narrowest a tile of the size is drawn at. */
const MAX_DOTS: Record<LauncherTileSize, number> = {
    [LauncherTileSize.Large]: 48,
    [LauncherTileSize.Medium]: 32,
    [LauncherTileSize.Small]: 10,
};

const BAR_HEIGHT: Record<LauncherTileSize, string> = {
    [LauncherTileSize.Large]: 'h-2.5',
    [LauncherTileSize.Medium]: 'h-2',
    [LauncherTileSize.Small]: 'h-1.5',
};

export const StatusDots = ({ parts, size }: Props): ReactElement => {
    const total = parts.reduce((sum, part) => sum + part.count, 0);
    if (total > MAX_DOTS[size]) {
        const bar = cn('flex w-full gap-px overflow-hidden rounded-full', BAR_HEIGHT[size]);
        return (
            <span className={bar}>
                {parts.filter((part) => part.count > 0).map((part) => (
                    <span
                        key={part.label}
                        className={part.fill}
                        style={{ flexGrow: part.count }}
                    />
                ))}
            </span>
        );
    }
    // The dots of a state are alike, so their place is all that tells them apart.
    const dots = parts.flatMap((part) => Array.from(
        { length: part.count },
        (_, index) => ({ key: `${part.label}:${index}`, fill: part.fill }),
    ));
    return (
        <span className={cn('flex flex-wrap', DOT_GAP[size])}>
            {dots.map((dot) => (
                <span
                    key={dot.key}
                    className={cn('shrink-0 rounded-full', DOT[size], dot.fill)}
                />
            ))}
        </span>
    );
};
